import { useParams } from 'react-router-dom';
import {
    RequestStatus,
    useServerErrorEffect,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';

import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import { HeaderComponent } from '../layout/components/HeaderComponent.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { ReadOnlyTextField } from '../ui/material-ui/ReadOnlyTextField.tsx';
import {
    BlockLabel,
    FormContentLayout,
    FormLeftColumn,
    FormRightColumn,
} from '../layout/components/form/FormLayout.tsx';
import { useQuery } from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { HeaderActionCloseButtons } from '../layout/components/HeaderActionCloseButtons.tsx';
import { openNewTab } from 'dg-web-shared/common/utils/Navigation.tsx';
import { HeaderCancelSaveButtons } from '../layout/components/HeaderCancelSaveButtons.tsx';
import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { PlayCircle, StopCircle } from '@mui/icons-material';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import { PaymentChannelLocalized } from '../common/components/PaymentChannelLocalized.tsx';
import {
    getTransactionAmount,
    getZoneName,
    LicensePlate,
    OnstreetRunningTransaction,
    OnstreetTerminatedTransaction,
    TomTransaction,
    Transaction,
    TransactionType,
} from './OperatorOnstreetTransactionShared.tsx';
import {
    isInputAmountRefundable,
    OperatorRefund,
    parseChf,
    TransactionRefundBlockedExplanation,
    TransactionRefundInputValues,
    TransactionRefundPayload,
} from '../common/models/OperatorRefund.ts';
import {
    TransactionRefundInputFields,
    TransactionRefundResponseBanner,
} from '../common/components/TransactionRefund.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { OffstreetTransactionRefundBlocked } from '../offstreet-transactions/OperatorOffstreetTransactionShared.tsx';
import { operatorAppApiUrl } from '../api/Http.ts';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';

export function OperatorOnstreetTransactionContractDetail() {
    const { contractExternalId } = useParams<{ contractExternalId: string }>();
    const query = useQuery();
    const [OnstreetTransactionsState, refetchOnstreetTransaction] =
        useServerFetch<Transaction, { contractExternalId: string }, null>(
            context => ({
                url: `/ui-api/operator-account/onstreet-transactions/contract/${context.contractExternalId}`,
            }),
            contractExternalId
                ? { contractExternalId: contractExternalId }
                : null,
        );

    return (
        <OperatorRoutedModal
            variant={ModalVariant.STANDARD}
            render={controller => (
                <OperatorAsyncLoadedSection
                    requestState={OnstreetTransactionsState}
                    render={transaction => {
                        switch (transaction.type) {
                            case TransactionType.ONSTREET_RUNNING:
                                return (
                                    <RunningTransactionContractDetailContent
                                        transaction={transaction}
                                        onClose={controller.close}
                                    />
                                );
                            case TransactionType.ONSTREET_TERMINATED:
                                return (
                                    <TerminatedTransactionContractDetailContent
                                        transaction={transaction}
                                        onClose={controller.close}
                                        refetchTransaction={
                                            refetchOnstreetTransaction
                                        }
                                        onDirtyStateChange={
                                            controller.setShouldConfirmBeforeLeave
                                        }
                                    />
                                );
                            case TransactionType.TOM:
                                return (
                                    <TomTransactionContractDetailContent
                                        transaction={transaction}
                                        onClose={controller.close}
                                    />
                                );
                        }
                    }}
                />
            )}
            backUrl={`..?${query}`}
        />
    );
}

function RunningTransactionContractDetailContent({
    transaction,
    onClose,
}: {
    transaction: OnstreetRunningTransaction;
    onClose: () => void;
}) {
    return (
        <OperatorRoutedModalContent
            header={
                <HeaderComponent
                    title={
                        <Localized
                            de="Parkvorgang"
                            fr="Procédure"
                            it="Procedura"
                            en="Procedure"
                        />
                    }
                    editMode={false}
                    buttons={<HeaderActionCloseButtons onClose={onClose} />}
                />
            }
            body={
                <FormContentLayout>
                    <FormLeftColumn>
                        <TransactionStateField running={true} />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Identifikation"
                                    fr="Identification"
                                    it="Identificazione"
                                    en="Identification"
                                />
                            }
                            value={<LicensePlate transaction={transaction} />}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Beginn"
                                    fr="Début"
                                    it="Inizio"
                                    en="Start"
                                />
                            }
                            value={Formatter.dayMonthYearHourMinute(
                                DateTime.fromISO(transaction.validFrom),
                            )}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Ende"
                                    fr="Fin"
                                    it="Fine"
                                    en="End"
                                />
                            }
                            value={EmDash}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Dauer"
                                    fr="Durée"
                                    it="Durata"
                                    en="Duration"
                                />
                            }
                            value={EmDash}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Zone"
                                    fr="Zone"
                                    it="Zona"
                                    en="Zone"
                                />
                            }
                            value={getZoneName(transaction)}
                        />
                    </FormLeftColumn>
                    <FormRightColumn>
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Bezahlt"
                                    fr="Payé"
                                    it="Pagato"
                                    en="Paid"
                                />
                            }
                            value={EmDash}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Kanal"
                                    fr="Canal"
                                    it="Canale"
                                    en="Channel"
                                />
                            }
                            value={
                                <PaymentChannelLocalized
                                    paymentChannel={transaction.paymentChannel}
                                />
                            }
                        />
                        <TransactionFootnote transaction={transaction} />
                    </FormRightColumn>
                </FormContentLayout>
            }
        />
    );
}

function TerminatedTransactionContractDetailContent({
    transaction,
    onClose,
    refetchTransaction,
    onDirtyStateChange,
}: {
    transaction: OnstreetTerminatedTransaction;
    onClose: () => void;
    refetchTransaction: () => void;
    onDirtyStateChange: (shouldConfirm: boolean) => void;
}) {
    const { operatorExternalId } = useOperatorContext();

    const [openRefundResponseBanner, setOpenRefundResponseBanner] =
        useState(false);
    const [refundInputVisible, setRefundInputVisible] = useState(false);
    const [refundInputValues, setRefundInputValues] =
        useState<TransactionRefundInputValues>({
            amount: '',
            remark: '',
        });
    const [refundTransactionState, refundTransaction] = useServerWrite<
        TransactionRefundPayload,
        null
    >(({ contractExternalId }) => ({
        url: `/ui-api/operator-account/park-transaction/${contractExternalId}/refund`,
    }));

    function onRefundTransactionStateChange() {
        setOpenRefundResponseBanner(true);
        setRefundInputValues({
            amount: '',
            remark: '',
        });
        setRefundInputVisible(false);
    }

    useServerSuccessEffect(refundTransactionState, () => {
        refetchTransaction();
        onRefundTransactionStateChange();
    });

    useServerErrorEffect(
        refundTransactionState,
        onRefundTransactionStateChange,
    );

    useEffect(() => {
        onDirtyStateChange(refundInputVisible);
    }, [onDirtyStateChange, refundInputVisible]);

    const amountRappen = Math.round(parseChf(refundInputValues.amount) * 100);
    const isRefundable = isInputAmountRefundable(
        Math.round(transaction.priceChf * 100),
        amountRappen,
    );

    return (
        <OperatorRoutedModalContent
            header={
                <HeaderComponent
                    title={
                        <Localized
                            de="Parkvorgang"
                            fr="Procédure"
                            it="Procedura"
                            en="Procedure"
                        />
                    }
                    editMode={refundInputVisible}
                    buttons={
                        !refundInputVisible ? (
                            <HeaderActionCloseButtons
                                onClose={onClose}
                                dropdownItems={[
                                    {
                                        key: 'reimburse',
                                        label: (
                                            <Localized
                                                de="Zurückzahlen"
                                                fr="Rembourser"
                                                it="Rimborsa"
                                                en="Refund"
                                            />
                                        ),
                                        onClickCallback: () =>
                                            setRefundInputVisible(true),
                                        disabled:
                                            transaction.refundBlockedExplanation !==
                                            null,
                                        tooltip:
                                            transaction.refundBlockedExplanation ? (
                                                <OffstreetTransactionRefundBlocked
                                                    refundBlockedExplanation={
                                                        transaction.refundBlockedExplanation
                                                    }
                                                />
                                            ) : undefined,
                                    },
                                    {
                                        key: 'download-receipt',
                                        label: (
                                            <Localized
                                                de="Quittung herunterladen"
                                                fr="Télécharger le reçu"
                                                it="Scarica ricevuta"
                                                en="Download receipt"
                                            />
                                        ),
                                        onClickCallback: () => {
                                            openNewTab(
                                                urlWithOperatorExternalId(
                                                    operatorAppApiUrl(
                                                        `/park-transaction/${transaction.contractExternalId}/receipt`,
                                                    ),
                                                    operatorExternalId,
                                                ),
                                            );
                                        },
                                        disabled: false,
                                    },
                                ]}
                            />
                        ) : (
                            <HeaderCancelSaveButtons
                                onCancel={() => {
                                    setRefundInputValues({
                                        amount: '',
                                        remark: '',
                                    });
                                    setRefundInputVisible(false);
                                }}
                                onSave={() => {
                                    if (isRefundable) {
                                        refundTransaction({
                                            contractExternalId:
                                                transaction.contractExternalId,
                                            amountRappen: amountRappen,
                                            remark: refundInputValues.remark,
                                        });
                                    }
                                }}
                                saveDisabled={!isRefundable}
                                loading={
                                    refundTransactionState.status ===
                                    RequestStatus.PENDING
                                }
                            />
                        )
                    }
                />
            }
            body={
                <FormContentLayout>
                    <FormLeftColumn>
                        <TransactionStateField running={false} />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Identifikation"
                                    fr="Identification"
                                    it="Identificazione"
                                    en="Identification"
                                />
                            }
                            value={<LicensePlate transaction={transaction} />}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Beginn"
                                    fr="Début"
                                    it="Inizio"
                                    en="Start"
                                />
                            }
                            value={Formatter.dayMonthYearHourMinute(
                                DateTime.fromISO(transaction.validFrom),
                            )}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Ende"
                                    fr="Fin"
                                    it="Fine"
                                    en="End"
                                />
                            }
                            value={Formatter.dayMonthYearHourMinute(
                                DateTime.fromISO(transaction.validTo),
                            )}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Dauer"
                                    fr="Durée"
                                    it="Durata"
                                    en="Duration"
                                />
                            }
                            value={transaction.duration}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Zone"
                                    fr="Zone"
                                    it="Zona"
                                    en="Zone"
                                />
                            }
                            value={getZoneName(transaction)}
                        />
                    </FormLeftColumn>
                    <FormRightColumn>
                        <TransactionRefundResponseBanner
                            open={openRefundResponseBanner}
                            onClose={() => setOpenRefundResponseBanner(false)}
                            success={
                                refundTransactionState.status ===
                                RequestStatus.SUCCESS
                            }
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Bezahlt"
                                    fr="Payé"
                                    it="Pagato"
                                    en="Paid"
                                />
                            }
                            value={getTransactionAmount(transaction)}
                        />
                        {refundInputVisible && (
                            <TransactionRefundInputFields
                                price={transaction.priceChf}
                                refundInputValues={refundInputValues}
                                setRefundInputValues={setRefundInputValues}
                            />
                        )}
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Kanal"
                                    fr="Canal"
                                    it="Canale"
                                    en="Channel"
                                />
                            }
                            value={
                                <PaymentChannelLocalized
                                    paymentChannel={transaction.paymentChannel}
                                />
                            }
                        />
                        {transaction.refund && (
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Rückzahlung"
                                        fr="Remboursement"
                                        it="Rimborso"
                                        en="Refund"
                                    />
                                }
                                value={
                                    <TransactionRefund
                                        refund={transaction.refund}
                                    />
                                }
                            />
                        )}
                        <TransactionFootnote transaction={transaction} />
                    </FormRightColumn>
                </FormContentLayout>
            }
        />
    );
}

function TomTransactionContractDetailContent({
    transaction,
    onClose,
}: {
    transaction: TomTransaction;
    onClose: () => void;
}) {
    const { operatorExternalId } = useOperatorContext();
    return (
        <OperatorRoutedModalContent
            header={
                <HeaderComponent
                    title={
                        <Localized
                            de="Parkvorgang"
                            fr="Procédure"
                            it="Procedura"
                            en="Procedure"
                        />
                    }
                    editMode={false}
                    buttons={
                        <HeaderActionCloseButtons
                            onClose={onClose}
                            dropdownItems={[
                                {
                                    key: 'reimburse',
                                    label: (
                                        <Localized
                                            de="Zurückzahlen"
                                            fr="Rembourser"
                                            it="Rimborsa"
                                            en="Refund"
                                        />
                                    ),
                                    onClickCallback: () => {},
                                    disabled: true,
                                    tooltip: (
                                        <OffstreetTransactionRefundBlocked
                                            refundBlockedExplanation={
                                                TransactionRefundBlockedExplanation.NO_DIGITAL_PURCHASE
                                            }
                                        />
                                    ),
                                },
                                {
                                    key: 'download-receipt',
                                    label: (
                                        <Localized
                                            de="Quittung herunterladen"
                                            fr="Télécharger le reçu"
                                            it="Scarica ricevuta"
                                            en="Download receipt"
                                        />
                                    ),
                                    onClickCallback: () => {
                                        openNewTab(
                                            urlWithOperatorExternalId(
                                                operatorAppApiUrl(
                                                    `/park-transaction/${transaction.contractExternalId}/receipt`,
                                                ),
                                                operatorExternalId,
                                            ),
                                        );
                                    },
                                    disabled: false,
                                },
                            ]}
                        />
                    }
                />
            }
            body={
                <FormContentLayout>
                    <FormLeftColumn>
                        <TransactionStateField
                            running={
                                // TOMeco transactions that have been paid into the future
                                DateTime.fromISO(transaction.validTo) >
                                DateTime.now()
                            }
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Identifikation"
                                    fr="Identification"
                                    it="Identificazione"
                                    en="Identification"
                                />
                            }
                            value={<LicensePlate transaction={transaction} />}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Beginn"
                                    fr="Début"
                                    it="Inizio"
                                    en="Start"
                                />
                            }
                            value={Formatter.dayMonthYearHourMinute(
                                DateTime.fromISO(transaction.validFrom),
                            )}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Ende"
                                    fr="Fin"
                                    it="Fine"
                                    en="End"
                                />
                            }
                            value={Formatter.dayMonthYearHourMinute(
                                DateTime.fromISO(transaction.validTo),
                            )}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Dauer"
                                    fr="Durée"
                                    it="Durata"
                                    en="Duration"
                                />
                            }
                            value={transaction.duration}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Zone"
                                    fr="Zone"
                                    it="Zona"
                                    en="Zone"
                                />
                            }
                            value={getZoneName(transaction)}
                        />
                    </FormLeftColumn>
                    <FormRightColumn>
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Bezahlt"
                                    fr="Payé"
                                    it="Pagato"
                                    en="Paid"
                                />
                            }
                            value={getTransactionAmount(transaction)}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Kanal"
                                    fr="Canal"
                                    it="Canale"
                                    en="Channel"
                                />
                            }
                            value={
                                <PaymentChannelLocalized
                                    paymentChannel={transaction.paymentChannel}
                                />
                            }
                        />
                        {transaction.refund && (
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Rückzahlung"
                                        fr="Remboursement"
                                        it="Rimborso"
                                        en="Refund"
                                    />
                                }
                                value={
                                    <TransactionRefund
                                        refund={transaction.refund}
                                    />
                                }
                            />
                        )}
                        <TransactionFootnote transaction={transaction} />
                    </FormRightColumn>
                </FormContentLayout>
            }
        />
    );
}

function TransactionStateField({ running }: { running: boolean }) {
    if (running) {
        return (
            <ReadOnlyTextField
                label={
                    <Localized de="Status" fr="Statut" it="Stato" en="State" />
                }
                value={
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '4px',
                        }}
                    >
                        <PlayCircle color={'success'} />
                        <Localized
                            de="laufend"
                            fr="en cours"
                            it="in corso"
                            en="running"
                        />
                    </Box>
                }
            />
        );
    } else {
        return (
            <ReadOnlyTextField
                label={
                    <Localized de="Status" fr="Statut" it="Stato" en="State" />
                }
                value={
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '4px',
                        }}
                    >
                        <StopCircle color={'secondary'} />
                        <Localized
                            de="abgeschlossen"
                            fr="terminée"
                            it="completata"
                            en="completed"
                        />
                    </Box>
                }
            />
        );
    }
}

function TransactionRefund({ refund }: { refund: OperatorRefund }) {
    return (
        <>
            {`CHF ${(refund.refundAmountRappen / 100).toFixed(2)}`}
            <br />
            {Formatter.dayMonthYearHourMinute(
                DateTime.fromISO(refund.refundedAt),
            )}
            <br />
            {refund.remark}
            <br />
            {refund.refundedBy}
        </>
    );
}

function TransactionFootnote({ transaction }: { transaction: Transaction }) {
    return (
        <Box
            sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'end',
            }}
        >
            <BlockLabel
                label={
                    <Localized
                        de="Erstellt am"
                        fr="Créé le"
                        it="Creato il"
                        en="Created at"
                    />
                }
            >
                {Formatter.dayMonthYearHourMinute(
                    DateTime.fromISO(transaction.createdAt),
                )}
            </BlockLabel>
            <BlockLabel label="ID">{transaction.contractExternalId}</BlockLabel>
        </Box>
    );
}
